import { PlaybookTransformItem, PlaybookMapping, PlaybookEnrichment } from '@/types/playbook';
import { UnifiedSchema } from '@/types/schema';
import { PlaybookAIMappingResult } from './playbookAIMappingService';
import { MappingConflict, MappingSummaryData } from '@/components/dialogs/MappingSummaryDialog';

export interface MappingCandidate {
  inputPath: string;
  inputName: string;
  outputPath: string;
  outputName: string;
  certaintyScore?: number;
  mappingType: 'direct' | 'ai' | 'no-match';
  transformId?: string;
}

/**
 * Analyzes mapping results to detect conflicts where multiple inputs map to the same output
 * or multiple outputs map from the same input (for Integration playbooks)
 */
export const detectMappingConflicts = (
  mappingResults: PlaybookAIMappingResult[],
  inputSchema: UnifiedSchema | null,
  outputSchema: UnifiedSchema | null,
  existingTransforms: PlaybookTransformItem[],
  mappingType: 'auto' | 'ai' = 'auto',
  baselineSchema: 'input' | 'output' = 'input'
): MappingSummaryData => {
  const conflicts: MappingConflict[] = [];
  const successfulMappings: MappingCandidate[] = [];
  const unmappedProperties: Array<{ path: string; name: string; type: 'input' | 'output' }> = [];
  
  // Helper function to get parameter name from schema
  const getParameterName = (schema: UnifiedSchema | null, path: string): string => {
    if (!schema) return path.split('.').pop() || path;
    const param = schema.parameters.find(p => p.path === path);
    return param?.friendlyName || param?.codeName || path.split('.').pop() || path;
  };

  // Group mapping results by the appropriate path based on baseline schema
  // For input baseline: group by output path (multiple inputs -> same output)
  // For output baseline: group by input path (multiple outputs <- same input)
  const conflictGroups = new Map<string, MappingCandidate[]>();
  
  // Process mapping results
  mappingResults.forEach(result => {
    if (result.matchStatus === 'matched' && result.normalizedPath) {
      const candidate: MappingCandidate = {
        inputPath: result.inputPath,
        inputName: getParameterName(inputSchema, result.inputPath),
        outputPath: result.normalizedPath,
        outputName: getParameterName(outputSchema, result.normalizedPath),
        certaintyScore: result.certaintyScore,
        mappingType: result.isRegex ? 'ai' : (result.certaintyScore === 1.0 ? 'direct' : 'ai')
      };

      // Group by the appropriate path based on baseline schema
      const groupingKey = baselineSchema === 'output' ? result.inputPath : result.normalizedPath;
      
      if (!conflictGroups.has(groupingKey)) {
        conflictGroups.set(groupingKey, []);
      }
      conflictGroups.get(groupingKey)!.push(candidate);
    } else {
      // Unmapped property - type depends on baseline schema
      const unmappedType = baselineSchema === 'output' ? 'output' : 'input';
      const unmappedPath = baselineSchema === 'output' ? result.normalizedPath || result.inputPath : result.inputPath;
      const unmappedSchema = baselineSchema === 'output' ? outputSchema : inputSchema;
      
      unmappedProperties.push({
        path: unmappedPath,
        name: getParameterName(unmappedSchema, unmappedPath),
        type: unmappedType
      });
    }
  });

  // Check for conflicts and successful mappings
  conflictGroups.forEach((candidates, groupingKey) => {
    if (candidates.length > 1) {
      // Conflict detected
      if (baselineSchema === 'output') {
        // For output baseline: multiple outputs mapping from same input
        conflicts.push({
          outputPath: groupingKey, // This is actually the input path for output baseline
          outputName: getParameterName(inputSchema, groupingKey),
          candidates: candidates.map(c => ({
            inputPath: c.outputPath, // Swap for output baseline conflicts
            inputName: c.outputName,
            certaintyScore: c.certaintyScore,
            mappingType: c.mappingType,
            transformId: c.transformId
          }))
        });
      } else {
        // For input baseline: multiple inputs mapping to same output
        conflicts.push({
          outputPath: groupingKey,
          outputName: candidates[0].outputName,
          candidates: candidates.map(c => ({
            inputPath: c.inputPath,
            inputName: c.inputName,
            certaintyScore: c.certaintyScore,
            mappingType: c.mappingType,
            transformId: c.transformId
          }))
        });
      }
    } else if (candidates.length === 1) {
      // Successful mapping
      successfulMappings.push(candidates[0]);
    }
  });

  // Find unmapped properties based on baseline schema
  if (baselineSchema === 'output' && inputSchema) {
    // For output baseline, find unmapped input properties
    const mappedInputPaths = new Set([
      ...Array.from(conflictGroups.keys()),
      ...existingTransforms
        .filter(t => t.type === 'mapping')
        .map(t => (t.content as PlaybookMapping).inputKey)
        .filter(Boolean)
    ]);

    inputSchema.parameters.forEach(param => {
      if (!mappedInputPaths.has(param.path)) {
        unmappedProperties.push({
          path: param.path,
          name: param.friendlyName || param.codeName,
          type: 'input'
        });
      }
    });
  } else if (outputSchema) {
    // For input baseline, find unmapped output properties
    const mappedOutputPaths = new Set([
      ...Array.from(conflictGroups.keys()),
      ...existingTransforms
        .filter(t => t.type === 'mapping' || t.type === 'enrichment')
        .map(t => {
          if (t.type === 'mapping') {
            return (t.content as PlaybookMapping).outputKey;
          } else {
            return (t.content as PlaybookEnrichment).outputKey;
          }
        })
        .filter(Boolean)
    ]);

    outputSchema.parameters.forEach(param => {
      if (!mappedOutputPaths.has(param.path)) {
        unmappedProperties.push({
          path: param.path,
          name: param.friendlyName || param.codeName,
          type: 'output'
        });
      }
    });
  }

  return {
    totalProcessed: mappingResults.length,
    successfulMappings: successfulMappings.length,
    conflicts,
    unmappedProperties,
    mappingType
  };
};

/**
 * Analyzes existing transforms to detect conflicts where multiple inputs map to the same output
 */
export const detectExistingTransformConflicts = (
  transforms: PlaybookTransformItem[],
  inputSchema: UnifiedSchema | null,
  outputSchema: UnifiedSchema | null
): MappingConflict[] => {
  const conflicts: MappingConflict[] = [];
  
  // Helper function to get parameter name from schema
  const getParameterName = (schema: UnifiedSchema | null, path: string): string => {
    if (!schema) return path.split('.').pop() || path;
    const param = schema.parameters.find(p => p.path === path);
    return param?.friendlyName || param?.codeName || path.split('.').pop() || path;
  };

  // Group transforms by output path
  const outputPathGroups = new Map<string, Array<{
    inputPath: string;
    inputName: string;
    transformId: string;
    mappingType: 'direct' | 'ai' | 'no-match';
    certaintyScore?: number;
  }>>();

  transforms.forEach(transform => {
    let outputPath: string | undefined;
    let inputPath: string | undefined;

    if (transform.type === 'mapping') {
      const mapping = transform.content as PlaybookMapping;
      outputPath = mapping.outputKey;
      inputPath = mapping.inputKey;
    } else if (transform.type === 'enrichment') {
      const enrichment = transform.content as PlaybookEnrichment;
      outputPath = enrichment.outputKey;
      inputPath = ''; // Enrichments don't have input paths
    }

    if (outputPath) {
      if (!outputPathGroups.has(outputPath)) {
        outputPathGroups.set(outputPath, []);
      }

      // Normalize mappingType to expected values
      let normalizedMappingType: 'direct' | 'ai' | 'no-match' = 'direct';
      if (transform.mappingType) {
        switch (transform.mappingType) {
          case 'ai':
          case 'regex':
            normalizedMappingType = 'ai';
            break;
          case 'no-match':
            normalizedMappingType = 'no-match';
            break;
          default:
            normalizedMappingType = 'direct';
        }
      }

      outputPathGroups.get(outputPath)!.push({
        inputPath: inputPath || '',
        inputName: inputPath ? getParameterName(inputSchema, inputPath) : 'Direct Value',
        transformId: transform.id,
        mappingType: normalizedMappingType,
        certaintyScore: transform.__meta?.certaintyScore
      });
    }
  });

  // Check for conflicts
  outputPathGroups.forEach((candidates, outputPath) => {
    if (candidates.length > 1) {
      conflicts.push({
        outputPath,
        outputName: getParameterName(outputSchema, outputPath),
        candidates: candidates.map(c => ({
          inputPath: c.inputPath,
          inputName: c.inputName,
          certaintyScore: c.certaintyScore,
          mappingType: c.mappingType,
          transformId: c.transformId
        }))
      });
    }
  });

  return conflicts;
};

/**
 * Resolves mapping conflicts by removing conflicting transforms and keeping only the chosen ones
 */
export const resolveMappingConflicts = (
  conflicts: MappingConflict[],
  resolutions: { [outputPath: string]: string }, // outputPath -> chosen inputPath
  transforms: PlaybookTransformItem[]
): {
  transformsToDelete: string[];
  transformsToUpdate: { [transformId: string]: Partial<PlaybookTransformItem> };
} => {
  const transformsToDelete: string[] = [];
  const transformsToUpdate: { [transformId: string]: Partial<PlaybookTransformItem> } = {};

  conflicts.forEach(conflict => {
    const chosenInputPath = resolutions[conflict.outputPath];
    if (!chosenInputPath) return;

    // Find the chosen candidate
    const chosenCandidate = conflict.candidates.find(c => c.inputPath === chosenInputPath);
    if (!chosenCandidate) return;

    // Mark other candidates for deletion
    conflict.candidates.forEach(candidate => {
      if (candidate.inputPath !== chosenInputPath && candidate.transformId) {
        transformsToDelete.push(candidate.transformId);
      }
    });

    // If the chosen candidate has a transformId, ensure it's properly configured
    if (chosenCandidate.transformId) {
      const transform = transforms.find(t => t.id === chosenCandidate.transformId);
      if (transform) {
        // Ensure the transform has the correct mapping
        if (transform.type === 'mapping') {
          transformsToUpdate[chosenCandidate.transformId] = {
            content: {
              ...(transform.content as PlaybookMapping),
              inputKey: chosenInputPath,
              outputKey: conflict.outputPath,
              transforms: [{ type: 'DirectAssign' }]
            },
            inputPath: chosenInputPath,
            outputPath: conflict.outputPath,
            mappingType: chosenCandidate.mappingType
          };
        }
      }
    }
  });

  return { transformsToDelete, transformsToUpdate };
};

/**
 * Creates summary data from auto mapping results (direct matches)
 */
export const createAutoMappingSummary = (
  mappingResults: PlaybookAIMappingResult[],
  inputSchema: UnifiedSchema | null,
  outputSchema: UnifiedSchema | null,
  existingTransforms: PlaybookTransformItem[],
  baselineSchema: 'input' | 'output' = 'input'
): MappingSummaryData => {
  return detectMappingConflicts(mappingResults, inputSchema, outputSchema, existingTransforms, 'auto', baselineSchema);
};

/**
 * Creates summary data from AI mapping results
 */
export const createAIMappingSummary = (
  mappingResults: PlaybookAIMappingResult[],
  inputSchema: UnifiedSchema | null,
  outputSchema: UnifiedSchema | null,
  existingTransforms: PlaybookTransformItem[],
  baselineSchema: 'input' | 'output' = 'input'
): MappingSummaryData => {
  return detectMappingConflicts(mappingResults, inputSchema, outputSchema, existingTransforms, 'ai', baselineSchema);
};
